import React, { useState } from "react";
import { LayoutDashboard, Package, Users, BarChart3 } from "lucide-react";
import BatchAnalytics from "./BatchAnalytics";

export default function AnalyticsDashboard() {
  // Simple state manager to control which module's charts are actively visible
  const [activeTab, setActiveTab] = useState("batch");

  return (
    <div className="enterprise-analytics-workspace animate-fade-in bg-light min-vh-100">
      
      {/* 1. SIMPLE & CLEAR SYSTEM NAVBAR */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center border-bottom bg-white py-2 px-3 mb-3 shadow-sm sticky-top">
        <div>
          <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-tight" style={{ fontSize: "0.8rem" }}>
            System Analytics Hub
          </h6>
          <div className="text-muted extra-small text-uppercase mt-0.5" style={{ fontSize: "0.58rem" }}>
            Overview and Module Status
          </div>
        </div>
        
        {/* RIGHT SIDE: SIMPLE AND UNDERSTANDABLE SYSTEM FLAG */}
        <div className="d-none d-sm-flex align-items-center">
          <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2 py-1 text-uppercase fw-bold d-flex align-items-center gap-1" style={{ fontSize: "0.55rem" }}>
            <span className="simple-status-dot"></span> System Online
          </span>
        </div>
      </div>

      {/* 2. MAIN VIEWPORT */}
      <div className="workspace-inner-padding">
        
        {/* MODULE NAVIGATION TABS */}
        <div className="card border-0 shadow-sm bg-white rounded-3 p-1.5 mb-3">
          <div className="nav nav-pills d-flex gap-1">
            <button 
              onClick={() => setActiveTab("batch")}
              className={`btn btn-xs flex-fill d-flex align-items-center justify-content-center gap-1.5 fw-bold ${activeTab === "batch" ? "btn-primary" : "btn-light text-muted border-0"}`}
            >
              <BarChart3 size={11} />
              BATCHES
            </button>
            <button 
              onClick={() => setActiveTab("product")}
              className={`btn btn-xs flex-fill d-flex align-items-center justify-content-center gap-1.5 fw-bold ${activeTab === "product" ? "btn-primary" : "btn-light text-muted border-0"}`}
            >
              <Package size={11} />
              PRODUCTS
            </button>
            <button 
              onClick={() => setActiveTab("user")}
              className={`btn btn-xs flex-fill d-flex align-items-center justify-content-center gap-1.5 fw-bold ${activeTab === "user" ? "btn-primary" : "btn-light text-muted border-0"}`}
            >
              <Users size={11} />
              USERS
            </button>
          </div>
        </div>

        {/* COMPACT DASHBOARD DYNAMIC HEADER TITLE */}
        <div className="mb-3 px-1">
          <h5 className="fw-bold text-dark mb-0 tracking-tight text-uppercase" style={{ fontSize: "0.8rem" }}>
            {activeTab === "batch" && "Batch Operations Analytics"}
            {activeTab === "product" && "Product Catalog Metrics"}
            {activeTab === "user" && "User Account Metrics"}
          </h5>
          <div className="text-muted extra-small text-uppercase tracking-wider fw-bold" style={{ fontSize: "0.58rem" }}>
            {activeTab === "batch" && "Live production metrics & regulatory distributions"}
            {activeTab === "product" && "Inventory breakdowns & specification groups"}
            {activeTab === "user" && "Role profiles & access authorization logging"}
          </div>
        </div>

        {/* DYNAMIC METRICS ROUTER LAYER */}
        <div className="row">
          <div className="col-12">
            {activeTab === "batch" && <BatchAnalytics />}
            {activeTab === "product" && <PlaceholderMetricsCard moduleName="Product" />}
            {activeTab === "user" && <PlaceholderMetricsCard moduleName="User" />}
          </div>
        </div>
        
      </div>

      {/* COMPACT STYLES WITHOUT SCREEN SHIFT BREAKS */}
      <style>{`
        .enterprise-analytics-workspace {
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
          background-color: #f8fafc !important;
          background-image: radial-gradient(#cbd5e1 0.6px, transparent 0.6px);
          background-size: 16px 16px;
        }

        .workspace-inner-padding {
          padding: 0 1rem 2rem 1rem;
        }

        .extra-small {
          font-size: 0.65rem !important;
        }

        .gap-1.5 {
          gap: 0.35rem !important;
        }

        .btn-xs {
          padding: 0.25rem 0.5rem;
          font-size: 0.6rem;
          border-radius: 4px;
          transition: all 0.15s ease-in-out;
        }

        .simple-status-dot {
          width: 6px;
          height: 6px;
          background-color: #198754;
          border-radius: 50%;
          display: inline-block;
        }

        @media (max-width: 576px) {
          .workspace-inner-padding {
            padding: 0 0.5rem 1.5rem 0.5rem;
          }
        }
      `}</style>
    </div>
  );
}

/* ================= PRE-STYLED SYSTEM PLACEHOLDER ================= */

const PlaceholderMetricsCard = ({ moduleName }) => (
  <div className="card border-0 shadow-sm rounded-3 bg-white text-center py-5 px-3">
    <div className="card-body">
      <div className="spinner-grow spinner-grow-sm text-primary opacity-25 mb-2"></div>
      <h6 className="fw-bold text-dark uppercase mb-1" style={{ fontSize: "0.65rem" }}>
        {moduleName} Analytics Pipeline
      </h6>
      <p className="text-muted mb-0 extra-small uppercase fw-bold tracking-wider" style={{ fontSize: "0.55rem" }}>
        Database view initialization pending connection stream setup.
      </p>
    </div>
  </div>
);